import { widgetApi } from "../services/api";

/**
 * Typed wrappers over the `/widget/catalog/*` endpoints exposed by
 * raas-widgets-backend. The request flow primarily derives availability from the
 * pre-quote, but these are provided for progressive payout/payer discovery when
 * a richer compose step is needed. The widget unwraps `data.items` here.
 *
 * The `x-link-id` header is injected by the shared `widgetApi` interceptor.
 */

export type EffectiveStatus = "AVAILABLE" | "UNAVAILABLE";

export interface PayoutMethodCapability {
  payoutMethod: string;
  displayName: string;
  requiredFields: string[];
  supportedByCorridors: string[];
  effectiveStatus: EffectiveStatus;
}

export interface PayerCapability {
  payerCode: string;
  displayName: string;
  countryCode: string;
  payoutMethods: string[];
  supportedByCorridors: string[];
  effectiveStatus: EffectiveStatus;
  instructions?: string;
  accountPrefixes?: string[];
  constraints?: Record<string, unknown>;
}

export interface StateCapability {
  countryCode: string;
  stateCode: string;
  stateName: string;
  payoutMethods: string[];
  payerCodes?: string[];
  supportedByCorridors: string[];
  effectiveStatus: EffectiveStatus;
}

interface CatalogListResponse<T> {
  success: boolean;
  data: { items: T[] };
}

const toQueryString = (params: object): string => {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    usp.set(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export interface OriginCountryCapability {
  countryCode: string;
  countryName: string;
  supportedByCorridors: string[];
  effectiveStatus: EffectiveStatus;
  unavailableReasons?: string[];
}

export interface CatalogOriginCountriesInput {
  fundingMethod?: string;
  includeUnavailable?: boolean;
}

/**
 * Origin countries offered on the link. Powers the editable origin selector.
 * GET /widget/catalog/origin-countries
 */
export const getCatalogOriginCountries = async (
  input: CatalogOriginCountriesInput = {}
): Promise<OriginCountryCapability[]> => {
  const { data } = await widgetApi.get<
    CatalogListResponse<OriginCountryCapability>
  >(`/widget/catalog/origin-countries${toQueryString(input)}`);
  return data.data.items;
};

export interface CountryCapability {
  countryCode: string;
  countryName: string;
  /** Convenience: first currency in `currencyCodes` (backend field name). */
  currencyCode?: string;
  currencyCodes?: string[];
  /** @deprecated Use `currencyCode` — kept for older mocks only. */
  currency?: string;
  supportedByCorridors: string[];
  effectiveStatus: EffectiveStatus;
}

/** One picker row per country+currency pair. */
export interface DestinationOption {
  countryCode: string;
  countryName: string;
  currencyCode: string;
  supportedByCorridors: string[];
  effectiveStatus: EffectiveStatus;
}

export {
  corridorCurrencyCodesByDestination,
  currenciesForCountry,
  expandCountryCurrencyOptions,
  mergeCatalogWithCorridorCurrencies,
  pickDefaultDestinationCurrency,
} from "../utils/destinationOptions";

/** Resolve the destination currency for a catalog country row (origin 1:1). */
export const currencyForCountry = (
  country: CountryCapability | undefined
): string | undefined =>
  country?.currencyCode ??
  country?.currency ??
  country?.currencyCodes?.[0];

export interface CatalogCountriesInput {
  originCountryCode?: string;
  fundingMethod?: string;
  includeUnavailable?: boolean;
}

/**
 * Destination countries reachable from the origin. Powers the editable
 * destination selector on the transfer screen (Patrón B discovery).
 * GET /widget/catalog/countries
 */
export const getCatalogCountries = async (
  input: CatalogCountriesInput = {}
): Promise<CountryCapability[]> => {
  const { data } = await widgetApi.get<CatalogListResponse<CountryCapability>>(
    `/widget/catalog/countries${toQueryString(input)}`
  );
  return data.data.items;
};

export interface CatalogPayoutMethodsInput {
  originCountryCode?: string;
  destinationCountryCode: string;
  stateCode?: string;
  cityCode?: string;
  fundingMethod?: string;
  includeUnavailable?: boolean;
}

export const getCatalogPayoutMethods = async (
  input: CatalogPayoutMethodsInput
): Promise<PayoutMethodCapability[]> => {
  const { data } = await widgetApi.get<
    CatalogListResponse<PayoutMethodCapability>
  >(`/widget/catalog/payout-methods${toQueryString(input)}`);
  return data.data.items;
};

export interface CatalogPayersInput {
  originCountryCode?: string;
  destinationCountryCode: string;
  stateCode?: string;
  cityCode?: string;
  payoutMethod?: string;
  fundingMethod?: string;
  /** ISO 4217 — narrows to capabilities that list this destination currency. */
  currencyCode?: string;
  includeUnavailable?: boolean;
}

export const getCatalogPayers = async (
  input: CatalogPayersInput
): Promise<PayerCapability[]> => {
  const { data } = await widgetApi.get<CatalogListResponse<PayerCapability>>(
    `/widget/catalog/payers${toQueryString(input)}`
  );
  return data.data.items;
};

export interface CatalogStatesInput {
  originCountryCode?: string;
  payoutMethod?: string;
  payerCode?: string;
  fundingMethod?: string;
  includeUnavailable?: boolean;
}

export const getCatalogStates = async (
  countryCode: string,
  input: CatalogStatesInput = {}
): Promise<StateCapability[]> => {
  const { data } = await widgetApi.get<CatalogListResponse<StateCapability>>(
    `/widget/catalog/countries/${countryCode}/states${toQueryString(input)}`
  );
  return data.data.items;
};
